import { fetchFromApi } from "./fetchFromApi";
import { normalizeSquadPlayer } from "./squadPlayers";
import { scorerDistribution } from "./simulatedScorers";
const cache = new Map();
const standingsCache = new Map();
export function clearScorerCache() {
  cache.clear();
  standingsCache.clear();
}
async function read(path) {
  const rows = [];
  for (let page = 1; page <= 100; page++) {
    const r = await fetchFromApi(path + (page === 1 ? "" : `&page=${page}`)),
      j = await r.json();
    if (!r.ok || !Array.isArray(j.data)) throw Error("SQUAD_UNAVAILABLE");
    rows.push(...j.data);
    if (j.pagination?.has_more !== true) return rows;
  }
  throw Error("SQUAD_PAGE_LIMIT");
}
export function readScorerSquad(seasonId, teamId) {
  const key = `${seasonId}:${teamId}`,
    old = cache.get(key);
  if (old && old.until > Date.now()) return old.promise;
  const promise = read(
    `fetch-data-v3/squads/seasons/${seasonId}/teams/${teamId}?include=player.nationality;position;player.detailedPosition;details;team;season`
  ).catch((e) => {
    cache.delete(key);
    throw e;
  });
  cache.set(key, { promise, until: Date.now() + 300000 });
  return promise;
}
export async function loadScorerTeam(seasonId, leagueId, team, games) {
  try {
    const raw = await readScorerSquad(seasonId, team.id);
    const players = raw
      .filter(
        (p) =>
          Number(p.team_id) === Number(team.id) &&
          Number(p.season_id) === Number(seasonId)
      )
      .map((p) =>
        normalizeSquadPlayer(p, { seasonId, leagueId, teamId: team.id })
      );
    return { name: team.name, players: scorerDistribution(players, games) };
  } catch {
    return { name: team.name, players: [] };
  }
}
export async function loadOracleScorers(fixture) {
  if (!fixture.seasonId || !fixture.leagueId) return {};
  let games = {};
  try {
    let entry = standingsCache.get(fixture.seasonId);
    let request = entry?.until > Date.now() ? entry.promise : null;
    if (!request) {
      request = read(
        `fetch-data-v3/standings/seasons/${fixture.seasonId}?include=details`
      );
      standingsCache.set(fixture.seasonId, {
        promise: request,
        until: Date.now() + 300000,
      });
      request.catch(() => standingsCache.delete(fixture.seasonId));
    }
    const rows = await request;
    games = Object.fromEntries(
      rows.map((t) => [
        t.participant_id,
        t.details?.find((d) => Number(d.type_id) === 129)?.value,
      ])
    );
  } catch {}
  const [home, away] = await Promise.all(
    ["home", "away"].map((side) =>
      loadScorerTeam(
        fixture.seasonId,
        fixture.leagueId,
        fixture[side],
        games[fixture[side].id]
      )
    )
  );
  return { home: home.players, away: away.players };
}
export async function loadSeasonScorers(seasonId, leagueId, standings) {
  const teams = {};
  let index = 0;
  await Promise.all(
    Array.from({ length: 4 }, async () => {
      while (index < standings.length) {
        const t = standings[index++];
        teams[t.participant_id] = {
          ...(await loadScorerTeam(
            seasonId,
            leagueId,
            { id: t.participant_id, name: t.team_name },
            t.overall?.games_played
          )),
          actualComplete: false,
        };
      }
    })
  );
  try {
    const actual = await loadActualScorers(seasonId),
      goals = new Map();
    for (const p of actual) {
      if (
        !Number.isFinite(Number(p.total)) ||
        Number(p.total) < 0 ||
        !p.player_id ||
        !p.participant_id ||
        !teams[p.participant_id]
      )
        throw Error("SCORER_VALUES");
      const key = `${p.participant_id}:${p.player_id}`;
      const previous = goals.get(key);
      goals.set(key, {
        playerId: Number(p.player_id),
        teamId: Number(p.participant_id),
        name: p.player?.display_name || p.player?.name || String(p.player_id),
        teamName: p.participant?.name || "",
        actualGoals: (previous?.actualGoals || 0) + Number(p.total),
      });
    }
    for (const [id, t] of Object.entries(teams)) {
      t.actualComplete = true;
      for (const p of t.players)
        p.actualGoals = goals.get(`${id}:${p.playerId}`)?.actualGoals ?? 0;
      t.baseline = [...goals.values()].filter(
        (p) =>
          String(p.teamId) === id &&
          !t.players.some((x) => x.playerId === p.playerId)
      );
    }
  } catch {
    /* Unknown actual counts remain unknown, never fabricate a complete title forecast. */
  }
  return teams;
}

// Same provider source/type as the existing betty top-scorer endpoint. Bound and exhaust pages.
export async function loadActualScorers(seasonId) {
  const rows = [],
    seen = new Set();
  for (let page = 1; page <= 100; page++) {
    const response = await fetchFromApi(
      `fetch-data-v3/topscorers/seasons/${seasonId}?include=player;participant;type&filters=seasonTopscorerTypes:208&per_page=50&page=${page}`
    );
    const body = await response.json();
    if (!response.ok || !Array.isArray(body.data))
      throw Error("SCORERS_UNAVAILABLE");
    for (const row of body.data) {
      if (
        Number(row.season_id) !== Number(seasonId) ||
        Number(row.type_id) !== 208
      )
        throw Error("SCORER_SCOPE");
      const key =
        row.id ?? `${row.player_id}:${row.participant_id}:${row.stage_id}`;
      if (!seen.has(key)) {
        seen.add(key);
        rows.push(row);
      }
    }
    if (body.pagination?.has_more === false) return rows;
    if (body.pagination?.has_more !== true)
      throw Error("SCORER_PAGINATION_UNKNOWN");
  }
  throw Error("SCORER_PAGE_LIMIT");
}
